<template>
  <div>
    <div class="page-header">
      <div class="container d-flex justify-content-start px-md-4 px-3">
        <h3>Authentication Information</h3>
      </div>
    </div>
    <div v-if="$auth.user" class="container mb-5 mt-3">
      <div class="row">
        <!-- User Information -->
        <div class="col-lg-6 col-12 mb-4">
          <div class="card">
            <h5 class="card-header">User Information</h5>
            <div class="card-body">
              <div class="mb-3">
                <strong>Name:</strong> {{ $auth.user.name }}
              </div>
              <div class="mb-3">
                <strong>Email:</strong> {{ $auth.user.email }}
              </div>
              <div class="mb-3">
                <strong>User ID:</strong> 
                <code class="text-break">{{ $auth.user.sub }}</code>
              </div>
              <div v-if="$auth.user.email_verified !== undefined" class="mb-3">
                <strong>Email Verified:</strong> 
                <span :class="$auth.user.email_verified ? 'text-success' : 'text-danger'">
                  {{ $auth.user.email_verified ? 'Yes' : 'No' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Token Information -->
        <div class="col-lg-6 col-12 mb-4">
          <div class="card">
            <h5 class="card-header">Token Information</h5>
            <div class="card-body">
              <div v-if="accessTokenExpiry" class="mb-3">
                <strong>Access Token Expires:</strong> 
                <span :class="isTokenExpiringSoon ? 'text-warning' : 'text-success'">
                  {{ formatDate(Math.floor(accessTokenExpiry.getTime() / 1000)) }}
                </span>
                <small class="text-muted d-block">
                  (Used for API calls and session monitoring)
                </small>
              </div>
              <div v-if="tokenClaims" class="mb-3">
                <strong>ID Token Issued:</strong> {{ formatDate(tokenClaims.iat) }}
              </div>
              <div v-if="tokenClaims" class="mb-3">
                <strong>ID Token Expires:</strong> {{ formatDate(tokenClaims.exp) }}
                <small class="text-muted d-block">
                  (Contains user identity information)
                </small>
              </div>
              <div v-if="timeUntilExpiry" class="mb-3">
                <strong>Session Expires In:</strong>
                <span :class="isTokenExpiringSoon ? 'text-warning' : 'text-success'">
                  {{ timeUntilExpiry }}
                </span>
              </div>
              <div v-if="tokenClaims && tokenClaims.aud" class="mb-3">
                <strong>Audience:</strong> 
                <code class="text-break">{{ Array.isArray(tokenClaims.aud) ? tokenClaims.aud.join(', ') : tokenClaims.aud }}</code>
              </div>
              <div v-if="tokenClaims && tokenClaims.iss" class="mb-3">
                <strong>Issuer:</strong> 
                <code class="text-break">{{ tokenClaims.iss }}</code>
              </div>
            </div>
          </div>
        </div>

        <!-- Access Token -->
        <div class="col-12 mb-4">
          <div class="card">
            <h5 class="card-header d-flex justify-content-between align-items-center">
              Access Token
              <button 
                class="btn btn-sm btn-outline-secondary" 
                @click="refreshToken"
                :disabled="refreshing"
              >
                {{ refreshing ? 'Refreshing...' : 'Refresh Token' }}
              </button>
            </h5>
            <div class="card-body">
              <div v-if="accessToken" class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <strong>Token:</strong>
                  <button 
                    class="btn btn-sm btn-outline-primary" 
                    @click="copyToken"
                  >
                    Copy Token
                  </button>
                </div>
                <textarea 
                  class="form-control" 
                  rows="4" 
                  :value="accessToken" 
                  readonly
                  style="font-family: monospace; font-size: 12px;"
                ></textarea>
              </div>
              <div v-else-if="tokenError" class="alert alert-danger">
                <strong>Error loading token:</strong> {{ tokenError }}
              </div>
              <div v-else class="text-muted">
                Loading token...
              </div>
            </div>
          </div>
        </div>

        <!-- All Token Claims -->
        <div class="col-12 mb-4">
          <div class="card">
            <h5 class="card-header">All Token Claims</h5>
            <div class="card-body">
              <div v-if="tokenClaims">
                <h6>ID Token Claims:</h6>
                <pre class="bg-light p-3 text-wrap" style="font-size: 12px;">{{ JSON.stringify(tokenClaims, null, 2) }}</pre>
              </div>
              <div v-if="accessTokenClaims" class="mt-3">
                <h6>Access Token Claims:</h6>
                <pre class="bg-light p-3 text-wrap" style="font-size: 12px;">{{ JSON.stringify(accessTokenClaims, null, 2) }}</pre>
              </div>
              <div v-if="!tokenClaims && !accessTokenClaims" class="text-muted">
                Loading claims...
              </div>
            </div>
          </div>
        </div>

        <!-- Company Information (if available) -->
        <div v-if="$store.state.userCurrentCompany" class="col-12 mb-4">
          <div class="card">
            <h5 class="card-header">Current Company</h5>
            <div class="card-body">
              <div class="mb-3">
                <strong>Company Name:</strong> {{ $store.state.userCurrentCompany.companyName }}
              </div>
              <div class="mb-3">
                <strong>Company ID:</strong> {{ $store.state.userCurrentCompany.companyId }}
              </div>
              <div v-if="$store.state.loggedInUserPermissions.length > 0" class="mb-3">
                <strong>Permissions:</strong>
                <ul class="mt-2">
                  <li v-for="permission in $store.state.loggedInUserPermissions" :key="permission">
                    {{ permission }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="container mb-5 mt-3">
      <div class="alert alert-warning">
        You are not authenticated. Please log in to view authentication information.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AuthInfo',
  data() {
    return {
      accessToken: null,
      tokenClaims: null,
      accessTokenClaims: null,
      tokenError: null,
      refreshing: false,
      updateInterval: null
    }
  },
  computed: {
    timeUntilExpiry() {
      // Use access token expiry for consistency with auth monitoring
      if (!this.accessToken) return null
      
      try {
        const tokenParts = this.accessToken.split('.');
        if (tokenParts.length !== 3) return null;
        
        const payload = JSON.parse(atob(tokenParts[1]));
        if (!payload.exp) return null;
        
        const now = Math.floor(Date.now() / 1000)
        const exp = payload.exp
        const diff = exp - now
        
        if (diff <= 0) return 'Expired'
        
        const hours = Math.floor(diff / 3600)
        const minutes = Math.floor((diff % 3600) / 60)
        const seconds = diff % 60
        
        if (hours > 0) {
          return `${hours}h ${minutes}m ${seconds}s`
        } else if (minutes > 0) {
          return `${minutes}m ${seconds}s`
        } else {
          return `${seconds}s`
        }
      } catch (error) {
        console.error('Error parsing access token for expiry:', error);
        return 'Error parsing token'
      }
    },
    isTokenExpiringSoon() {
      if (!this.accessToken) return false
      
      try {
        const tokenParts = this.accessToken.split('.');
        if (tokenParts.length !== 3) return false;
        
        const payload = JSON.parse(atob(tokenParts[1]));
        if (!payload.exp) return false;
        
        const now = Math.floor(Date.now() / 1000)
        const exp = payload.exp
        const diff = exp - now
        
        // Consider expiring soon if less than 5 minutes (300 seconds)
        return diff < 300
      } catch (error) {
        console.error('Error parsing access token for expiry check:', error);
        return false
      }
    },
    accessTokenExpiry() {
      if (!this.accessToken) return null
      
      try {
        const tokenParts = this.accessToken.split('.');
        if (tokenParts.length !== 3) return null;
        
        const payload = JSON.parse(atob(tokenParts[1]));
        return payload.exp ? new Date(payload.exp * 1000) : null;
      } catch (error) {
        console.error('Error parsing access token expiry:', error);
        return null
      }
    }
  },
  async mounted() {
    await this.loadTokenInfo()
    
    // Update time until expiry every second
    this.updateInterval = setInterval(() => {
      this.$forceUpdate()
    }, 1000)
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
  methods: {
    async loadTokenInfo() {
      try {
        // Get access token
        this.accessToken = await this.$auth.getTokenSilently()
        
        // Get ID token claims
        this.tokenClaims = await this.$auth.getIdTokenClaims()
        
        // Decode access token claims manually
        if (this.accessToken) {
          try {
            const tokenParts = this.accessToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              this.accessTokenClaims = payload;
            }
          } catch (decodeError) {
            console.error('Error decoding access token:', decodeError);
            this.accessTokenClaims = { error: 'Failed to decode access token' };
          }
        }
        
        this.tokenError = null
      } catch (error) {
        console.error('Error loading token info:', error)
        this.tokenError = error.message || 'Unknown error'
      }
    },
    async refreshToken() {
      this.refreshing = true
      try {
        // Clear existing token and get a new one
        this.accessToken = await this.$auth.getTokenSilently({ 
          ignoreCache: true 
        })
        this.tokenClaims = await this.$auth.getIdTokenClaims()
        
        // Re-decode access token claims
        if (this.accessToken) {
          try {
            const tokenParts = this.accessToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              this.accessTokenClaims = payload;
            }
          } catch (decodeError) {
            console.error('Error decoding access token:', decodeError);
            this.accessTokenClaims = { error: 'Failed to decode access token' };
          }
        }
        
        this.tokenError = null
      } catch (error) {
        console.error('Error refreshing token:', error)
        this.tokenError = error.message || 'Unknown error'
      } finally {
        this.refreshing = false
      }
    },
    copyToken() {
      if (this.accessToken) {
        navigator.clipboard.writeText(this.accessToken).then(() => {
          // Could add a toast notification here if available
          alert('Token copied to clipboard!')
        }).catch(err => {
          console.error('Failed to copy token:', err)
        })
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return 'N/A'
      
      const date = new Date(timestamp * 1000)
      return date.toLocaleString()
    }
  }
}
</script>

<style scoped>
.text-break {
  word-break: break-all;
}

.card {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}

code {
  font-size: 0.875em;
  background-color: #f8f9fa;
  padding: 0.2em 0.4em;
  border-radius: 0.25rem;
}
</style> 